import type { ProgressAltCellAddress, ProgressAltRowAt } from './progressAltCursorLib';

/**
 * The half of a cell's address that has nowhere else to live.
 *
 * Which scope the panel is looking at is named by the route, in the same group and task slots every
 * other focused page uses. Which time bucket of it, and which of that cell's photos, have no route
 * slot — so they ride in the query string, where they are still linkable.
 *
 * Their own parameter names rather than the `photoId` set `useNavigateToPhoto` writes — that one is
 * already spoken for. `AdminScanPageNew` opens `ScanLargePhotoViewer` on sight of `photoId`, so
 * reusing it would put two viewers on screen at once.
 */

export const PROGRESS_ALT_PHOTO_PARAM = 'timelinePhoto';
export const PROGRESS_ALT_AT_PARAM = 'timelineAt';

const UNDATED_AT_VALUE = 'undated';

const parseAt = (raw: string | null): ProgressAltRowAt | undefined => {
  if (raw == null) return undefined;
  if (raw === UNDATED_AT_VALUE) return UNDATED_AT_VALUE;
  const periodStart = Number(raw);
  return Number.isFinite(periodStart) ? periodStart : undefined;
};

/**
 * Which bucket and which photo the URL is asking for, as far as it says.
 *
 * Either half may be missing — a link to a scope alone is a legitimate address — and either may be
 * stale, so nothing here is trusted. `resolveCursor` is what decides whether any of it still exists.
 */
export const readProgressAltCellAddress = (params: URLSearchParams): ProgressAltCellAddress => ({
  at: parseAt(params.get(PROGRESS_ALT_AT_PARAM)),
  photoId: params.get(PROGRESS_ALT_PHOTO_PARAM) ?? undefined,
});

/**
 * The same params with this cell's address written in, or cleared out when there is no address.
 *
 * Returns a fresh instance and leaves every other param alone — the panel's own state and the scan
 * workspace's sticky params both ride in the same query string.
 */
export const writeProgressAltCellAddress = (
  params: URLSearchParams,
  { at, photoId }: ProgressAltCellAddress = {},
): URLSearchParams => {
  const next = new URLSearchParams(params);
  if (at != null) next.set(PROGRESS_ALT_AT_PARAM, `${at}`);
  else next.delete(PROGRESS_ALT_AT_PARAM);
  if (photoId) next.set(PROGRESS_ALT_PHOTO_PARAM, photoId);
  else next.delete(PROGRESS_ALT_PHOTO_PARAM);
  return next;
};
